import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, Loader2, CalendarPlus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { trackView, trackConversion } from '@/lib/analytics';
import { parseBrazilDateTime, SAO_PAULO_TZ } from '@/lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FONT_MAP = {
  Inter: "'Inter', sans-serif",
  Georgia: "'Georgia', serif",
  'Playfair Display': "'Playfair Display', serif",
  'Roboto Mono': "'Roboto Mono', monospace",
  Poppins: "'Poppins', sans-serif",
};

const BG_PRESETS = {
  lavender_mist: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 50%, #f0e7fe 100%)',
  rose_quartz: 'linear-gradient(135deg, #fff1f2 0%, #fce7f3 50%, #fdf2f8 100%)',
  morning_sky: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
  sage_garden: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf9 100%)',
  warm_sand: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 40%, #fff7ed 100%)',
  pearl: 'linear-gradient(180deg, #fafafa 0%, #f5f5f4 50%, #fafaf9 100%)',
  soft_lilac: 'linear-gradient(180deg, #faf5ff 0%, #f3e8ff 60%, #faf5ff 100%)',
  ocean_breeze: 'linear-gradient(135deg, #ecfeff 0%, #e0f2fe 50%, #eff6ff 100%)',
  blush_peach: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 50%, #fef2f2 100%)',
  mint_cream: 'linear-gradient(180deg, #f0fdfa 0%, #ecfdf5 50%, #f0fdf4 100%)',
  twilight: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)',
  midnight: 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
};

function getBackgroundStyle(page) {
  const bgType = page?.bg_type || 'gradient';
  if (bgType === 'gradient') {
    return { background: `linear-gradient(${page?.bg_gradient_direction || 'to bottom right'}, ${page?.cor_fundo || '#f8fafc'}, ${page?.cor_fundo_2 || '#e5f6ff'})` };
  }
  if (bgType === 'preset' && page?.bg_preset && BG_PRESETS[page.bg_preset]) return { background: BG_PRESETS[page.bg_preset] };
  return { backgroundColor: page?.cor_fundo || '#f8fafc' };
}

function getButtonStyle(style = {}) {
  return {
    backgroundColor: style.cor_botao || '#ffffff',
    color: style.cor_fonte || '#333333',
    borderColor: style.cor_borda || '#ffffff',
    borderWidth: `${style.tamanho_borda ?? 2}px`,
    borderStyle: style.tipo_borda || 'solid',
    borderRadius: `${style.border_radius ?? 15}px`,
  };
}

function parseDate(dateStr) {
  if (!dateStr) return null;
  const date = parseBrazilDateTime(dateStr);
  if (!date) return null;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: SAO_PAULO_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(date).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  return { year: parseInt(parts.year, 10), month: parseInt(parts.month, 10) - 1, day: parseInt(parts.day, 10), time: `${parts.hour}:${parts.minute}` };
}

function fmtWeekday(y, m, d) {
  const date = new Date(y, m, d);
  return date.toLocaleDateString('pt-BR', { weekday: 'long' });
}

function fmtMonthYear(m, y) {
  const date = new Date(y, m, 15);
  return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

export default function AgendaPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [subForm, setSubForm] = useState({ whatsapp: '', email: '' });
  const [subSaving, setSubSaving] = useState(false);
  const [successModal, setSuccessModal] = useState(null); // { nome, gcal_url }

  useEffect(() => {
    document.title = 'Agenda';
    fetch(`${API}/agenda/${slug}`).then(r => {
      if (!r.ok) { setNotFound(true); setLoading(false); return; }
      return r.json();
    }).then(d => {
      if (d) { setData(d); document.title = `Agenda - ${d.app_name || d.church_name}`; trackView('agenda', slug, d.church_id); }
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  const handleSubscribe = async () => {
    if (!subForm.whatsapp.trim() || !subForm.email.trim()) { toast.error('Preencha WhatsApp e e-mail.'); return; }
    setSubSaving(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/agenda/${slug}/subscribe`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    xhr.onload = () => {
      setSubSaving(false);
      try {
        const result = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          setSubModal(false);
          setSuccessModal({ nome: result.nome, gcal_url: result.gcal_public_url || '' });
          trackConversion('agenda', slug, data?.church_id || '');
        } else {
          toast.error(result.detail || 'Erro ao inscrever');
        }
      } catch { toast.error('Erro ao processar resposta'); }
    };
    xhr.onerror = () => { setSubSaving(false); toast.error('Erro de conexão'); };
    xhr.send(JSON.stringify(subForm));
  };

  const formatPhone = (value) => {
    let digits = value.replace(/\D/g, '');
    if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
    digits = digits.slice(0, 11);
    if (digits.length <= 2) return digits.length ? `(${digits}` : '';
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const pc = data?.page_config || {};
  const bgStyle = getBackgroundStyle(pc);
  const fontFamily = FONT_MAP[pc.fonte] || FONT_MAP.Inter;
  const textColor = pc.cor_fonte_nome || '#333333';
  const secondaryTextColor = pc.cor_fonte_bio || '#333333';
  const iconBg = pc.cor_fundo_icone || '#2663eb';
  const iconColor = pc.cor_icone || '#ffffff';
  const buttonStyle = getButtonStyle(data?.button_style || {});

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={bgStyle}><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={bgStyle}>
      <Calendar className="w-16 h-16 text-slate-300 mb-4" />
      <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily }}>Página não encontrada</h1>
      <p className="text-slate-500 mt-2">A agenda que procura não existe ou foi desativada.</p>
      <Footer />
    </div>
  );

  // Group events by date
  const grouped = {};
  for (const event of (data.events || [])) {
    const p = parseDate(event.date);
    if (!p) continue;
    const key = `${p.year}-${String(p.month + 1).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
    if (!grouped[key]) grouped[key] = { ...p, events: [] };
    grouped[key].events.push({ ...event, time: p.time });
  }
  const dates = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen flex flex-col" style={{ ...bgStyle, fontFamily, color: textColor }}>
      <div className="max-w-2xl mx-auto w-full px-4 py-8 flex-1">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: iconBg }}>
            <Calendar className="w-7 h-7" style={{ color: iconColor }} />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: textColor }}>{data.link?.titulo || data.link?.title || data.app_name || data.church_name}</h1>
          <p className="text-sm mt-1 opacity-70" style={{ color: secondaryTextColor }}>{data.link?.descricao || data.link?.description || 'Agenda de Eventos'}</p>
        </div>

        {/* Subscribe button - only show if Google Calendar is configured or could be */}
        {data.gcal_public_url || data.gcal_available ? (
        <div className="text-center mb-8">
          <div className="relative inline-flex">
            <span className="absolute inset-0 animate-ping opacity-20" style={{ backgroundColor: buttonStyle.backgroundColor, borderRadius: buttonStyle.borderRadius }} />
            <span className="absolute inset-0 animate-pulse opacity-10" style={{ backgroundColor: buttonStyle.backgroundColor, borderRadius: buttonStyle.borderRadius }} />
            <Button data-testid="agenda-open-subscribe-button" onClick={() => setSubModal(true)} className="relative px-6 gap-2 shadow-lg" style={buttonStyle}>
              <CalendarPlus className="w-4 h-4" />Adicionar ao Google Agenda
            </Button>
          </div>
          <p className="text-xs mt-3 opacity-50">Clique e informe seus dados para adicionar a agenda ao seu Google Agenda.</p>
        </div>
        ) : null}

        {/* Timeline */}
        {dates.length > 0 ? (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />

            {dates.map((dateKey, di) => {
              const g = grouped[dateKey];
              const weekday = fmtWeekday(g.year, g.month, g.day);
              const monthYear = fmtMonthYear(g.month, g.year);
              return (
                <div key={dateKey} className={`relative ${di > 0 ? 'mt-6' : ''}`}>
                  {/* Date circle + label */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center z-10 text-white font-bold text-lg shadow-sm flex-shrink-0" style={{ backgroundColor: iconBg }}>
                      {g.day}
                    </div>
                    <div>
                      <p className="text-sm font-semibold capitalize" style={{ color: textColor }}>{weekday}</p>
                      <p className="text-xs opacity-50 capitalize">{monthYear}</p>
                    </div>
                  </div>

                  {/* Events for this date */}
                  <div className="ml-6 pl-6 border-l-0 space-y-2">
                    {g.events.map(event => (
                      <div key={event.event_id} className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/60 p-4 shadow-sm ml-0">
                        <h3 className="font-semibold text-base" style={{ color: textColor }}>{event.name}</h3>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          {event.tipo_nome && <Badge variant="outline" className="text-[10px]">{event.tipo_nome}</Badge>}
                          {event.time && <span className="flex items-center gap-1 text-xs opacity-60"><Clock className="w-3 h-3" />{event.time}</span>}
                        </div>
                        {event.description && <p className="text-sm mt-2 opacity-50">{event.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum evento público agendado.</p>
          </div>
        )}
      </div>

      {/* Subscribe Modal */}
      <Dialog open={subModal} onOpenChange={setSubModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><CalendarPlus className="w-5 h-5" />Adicionar ao Google Agenda</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">WhatsApp *</Label>
              <Input value={subForm.whatsapp} onChange={e => setSubForm(prev => ({ ...prev, whatsapp: formatPhone(e.target.value) }))} placeholder="(11) 99999-9999" data-testid="agenda-sub-whatsapp" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">E-mail *</Label>
              <Input type="email" value={subForm.email} onChange={e => setSubForm(prev => ({ ...prev, email: e.target.value }))} placeholder="seu@email.com" data-testid="agenda-sub-email" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button data-testid="agenda-subscribe-cancel-button" size="sm" variant="outline" onClick={() => setSubModal(false)}>Cancelar</Button>
              <Button data-testid="agenda-subscribe-submit-button" size="sm" onClick={handleSubscribe} disabled={subSaving} style={buttonStyle}>
                {subSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Inscrever
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Modal with Google Calendar link */}
      <Dialog open={!!successModal} onOpenChange={(v) => { if (!v) setSuccessModal(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-green-600" />Inscrição Realizada!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Parabéns, <strong>{successModal?.nome}</strong>! Sua inscrição foi registrada com sucesso. Clique no botão abaixo para adicionar a agenda de eventos ao seu Google Agenda:
            </p>
            {successModal?.gcal_url ? (
              <Button data-testid="agenda-success-open-google-calendar-button" className="w-full gap-2" style={buttonStyle} onClick={() => { window.open(successModal.gcal_url, '_blank'); }}>
                <CalendarPlus className="w-4 h-4" />Adicionar ao Google Agenda
              </Button>
            ) : (
              <p className="text-sm text-slate-400">A agenda do Google Calendar ainda não está disponível. Contacte a administração.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
